import {useCallback, useEffect, useMemo, useState} from 'react'
import {useApolloClient, useLazyQuery} from '@apollo/client'
import {Alert, Badge, Button, Form, Modal, Pagination, ProgressBar, Spinner, Table} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {useAsyncTaskPolling} from '../../../hooks/useAsyncTaskPolling'
import {UPDATE_POLICIES_BY_CARRIER_STR, UPDATE_POLICIES_BY_CARRIER_ASYNC_STR} from '../../../gql/mutations/policyMutation'
import {GET_ALL_CARRIERS_WITH_STATUS_LIST} from '../../../gql/queries/carriersQuery'
import {GET_CARRIER_IMPORT_ASYNC_TASK, GET_CARRIER_IMPORT_BATCH_UNMATCHED} from '../../../gql/queries/carrierImportQueries'
import {DateInput} from 'app/tfi/components/DateInput'
import {BadgeStatePolicy} from '../../onePolicy/components/BadgeStatePolicy'
import {exportUnmatchedCsv, CarrierImportUnmatchedRow} from './carrierImportExport'
import {parseId} from 'app/tfi/helpers'

// ─────────────────────────────────────────────────────────────────────────────
// Feature flag: set to false to revert to the legacy synchronous upload.
// When false, the app calls the original sync mutation and this file is the
// only thing that needs to change — no backend code is affected.
const USE_ASYNC_CARRIER_IMPORT = true
// ─────────────────────────────────────────────────────────────────────────────

interface CarrierImportSyncResult {
  updatePoliciesByCarrier: {
    success: boolean
    message: string
    processedCount: number
    matchedCount: number
    unmatchedCount: number
    errorsCount: number
    unmatchedRows: CarrierImportUnmatchedRow[]
  }
}

interface CarrierImportAsyncInitResult {
  updatePoliciesByCarrierAsync: {
    success: boolean
    message: string
    task?: {
      taskId: string
      status: string
      progressPercentage?: number | null
      progressMessage?: string | null
    } | null
  }
}

interface ImportResult {
  success: boolean
  message: string
  processedCount: number
  matchedCount: number
  unmatchedCount: number
  errorsCount: number
  unmatchedRows: CarrierImportUnmatchedRow[]
}

interface CarrierItem {
  id: string
  name: string
  enabled?: boolean
}

interface PolicyStatusItem {
  code: string
  title: string
  color?: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  carriers: CarrierItem[]
  policyStatuses: PolicyStatusItem[]
}

const SUPPORTED_CARRIERS = [
  'ambetter', 'oscar', 'blue cross', 'blue shield', 'bcbs', 'molina', 'anthem', 'imperial',
  'sentara', 'jefferson', 'independence', 'amerihealth', 'ameri health',
  'cigna', 'wellpoint', 'alliant', 'highmark', 'medica', 'select health', 'selecthealth',
]
const ANTHEM_CARRIER_FALLBACK: CarrierItem = {id: '17', name: 'Anthem', enabled: false}
const PAGE_SIZE = 25

export const ModalUpdatePoliciesByCarrier: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  carriers,
  policyStatuses,
}) => {
  const [carrierId, setCarrierId] = useState('')
  const [asOfDate, setAsOfDate] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [fetchedCarriers, setFetchedCarriers] = useState<CarrierItem[]>([])
  const [loadingCarriers, setLoadingCarriers] = useState(false)

  // Unified result state — populated by either sync or async path
  const [importResult, setImportResult] = useState<ImportResult | null>(null)

  // Async-specific state
  const [asyncTaskId, setAsyncTaskId] = useState<string | null>(null)
  const [asyncProgress, setAsyncProgress] = useState<{pct: number; msg: string} | null>(null)
  const [fetchingUnmatched, setFetchingUnmatched] = useState(false)

  const [uploadFn, {loading: isSyncUploading, error: syncError}] =
    useUploadFile<CarrierImportSyncResult>()
  const [uploadAsyncFn, {loading: isAsyncUploading}] =
    useUploadFile<CarrierImportAsyncInitResult>()

  const [fetchUnmatched] = useLazyQuery(GET_CARRIER_IMPORT_BATCH_UNMATCHED, {
    fetchPolicy: 'network-only',
  })

  const client = useApolloClient()

  // Polling hook — only active when asyncTaskId is set
  const {task: pollingTask, isPolling} = useAsyncTaskPolling({
    taskId: asyncTaskId,
    query: GET_CARRIER_IMPORT_ASYNC_TASK,
    dataKey: 'carrierImportAsyncTask',
    pollingInterval: 2000,
    onSuccess: async (resultStr) => {
      setAsyncTaskId(null)
      setAsyncProgress(null)
      try {
        const parsed = JSON.parse(resultStr)
        // Fetch unmatched rows from the DB using the batch_id from the task result
        let unmatchedRows: CarrierImportUnmatchedRow[] = []
        if (parsed.batch_id) {
          setFetchingUnmatched(true)
          try {
            const res = await fetchUnmatched({variables: {batchId: parsed.batch_id}})
            unmatchedRows = res.data?.carrierImportBatchUnmatched ?? []
          } catch {
            // Non-critical: show counts even if unmatched detail fails
          } finally {
            setFetchingUnmatched(false)
          }
        }
        setImportResult({
          success: parsed.success ?? true,
          message: parsed.message ?? 'Import completed',
          processedCount: parsed.processed_count ?? 0,
          matchedCount: parsed.matched_count ?? 0,
          unmatchedCount: parsed.unmatched_count ?? 0,
          errorsCount: parsed.errors_count ?? 0,
          unmatchedRows,
        })
      } catch {
        toast.success('Import completed successfully')
      }
    },
    onError: (errMsg) => {
      setAsyncTaskId(null)
      setAsyncProgress(null)
      toast.error(`Import failed: ${errMsg}`)
    },
  })

  // Keep progress bar in sync with polling task
  useEffect(() => {
    if (pollingTask) {
      setAsyncProgress({
        pct: pollingTask.progressPercentage ?? 0,
        msg: pollingTask.progressMessage ?? 'Processing...',
      })
    }
  }, [pollingTask])

  useEffect(() => {
    if (!isOpen) return

    setLoadingCarriers(true)
    const searchTerms = [
      'ambetter', 'oscar', 'blue cross', 'blue shield', 'bcbs', 'molina', 'anthem', 'imperial',
      'sentara', 'jefferson', 'independence', 'amerihealth', 'cigna', 'wellpoint',
      'alliant', 'highmark', 'medica', 'select health', 'selecthealth', 'united',
    ]

    Promise.all(
      searchTerms.map((name) =>
        client.query({
          query: GET_ALL_CARRIERS_WITH_STATUS_LIST,
          variables: {status: 'all', name, first: 20},
          fetchPolicy: 'network-only',
        })
      )
    )
      .then((results) => {
        const seen = new Set<string>()
        const merged: CarrierItem[] = []
        for (const result of results) {
          const edges = result.data?.allInsuranceOperators?.edges || []
          for (const edge of edges) {
            const node = edge?.node
            if (node && !seen.has(String(node.id))) {
              seen.add(String(node.id))
              merged.push(node)
            }
          }
        }
        setFetchedCarriers(merged)
      })
      .catch(() => {})
      .finally(() => setLoadingCarriers(false))
  }, [isOpen, client])

  const allCarriers: CarrierItem[] = useMemo(() => {
    const base = fetchedCarriers.length > 0 ? fetchedCarriers : carriers || []
    const hasAnthem = base.some((carrier) => {
      const name = carrier?.name?.trim().toLowerCase() || ''
      return String(carrier?.id) === ANTHEM_CARRIER_FALLBACK.id || name === 'anthem'
    })
    if (hasAnthem) return base
    return [...base, ANTHEM_CARRIER_FALLBACK]
  }, [carriers, fetchedCarriers])

  const supportedCarriers = useMemo(
    () =>
      allCarriers
        .filter((carrier) => {
          const name = carrier?.name?.toLowerCase() || ''
          if (name.includes('capital blue cross')) return false
          if (name.includes('cigna') && name !== 'cigna') return false
          if ((name.includes('amerihealth') || name.includes('ameri health')) && name.includes('caritas')) return false
          if (name === 'united healthcare') return true
          if (name === 'cigna') return true
          if (name === 'medica') return true
          return SUPPORTED_CARRIERS.some((sc) => name.includes(sc))
        })
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [allCarriers]
  )

  const statusTitleByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.title || ''
    }
    return map
  }, [policyStatuses])

  const statusColorByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.color || 'secondary'
    }
    return map
  }, [policyStatuses])

  const buildFormData = useCallback(
    (mutationStr: string) => {
      if (!carrierId || !asOfDate || !file) return null
      const formData = new FormData()
      formData.append('0', file, file.name)
      formData.append(
        'operations',
        JSON.stringify({
          query: mutationStr,
          variables: {carrierId: parseId(carrierId), asOfDate, file: null},
        })
      )
      formData.append('map', JSON.stringify({'0': ['variables.file']}))
      return formData
    },
    [carrierId, asOfDate, file]
  )

  const handleUpload = useCallback(async () => {
    if (!carrierId || !asOfDate || !file) {
      toast.error('Please fill all required fields')
      return
    }

    if (USE_ASYNC_CARRIER_IMPORT) {
      // ── Async path ──────────────────────────────────────────────────────────
      const formData = buildFormData(UPDATE_POLICIES_BY_CARRIER_ASYNC_STR)
      if (!formData) return
      try {
        const result = await uploadAsyncFn(formData)
        const payload = result?.updatePoliciesByCarrierAsync
        if (!payload?.success || !payload?.task?.taskId) {
          toast.error(payload?.message || 'Failed to start import task')
          return
        }
        setAsyncProgress({pct: 0, msg: 'Task queued...'})
        setAsyncTaskId(payload.task.taskId)
      } catch {
        // error already set by useUploadFile
      }
    } else {
      // ── Sync path (legacy — untouched) ──────────────────────────────────────
      const formData = buildFormData(UPDATE_POLICIES_BY_CARRIER_STR)
      if (!formData) return
      try {
        const result = await uploadFn(formData)
        const payload = result?.updatePoliciesByCarrier
        if (payload) {
          setImportResult({
            success: payload.success,
            message: payload.message,
            processedCount: payload.processedCount,
            matchedCount: payload.matchedCount,
            unmatchedCount: payload.unmatchedCount,
            errorsCount: payload.errorsCount,
            unmatchedRows: payload.unmatchedRows ?? [],
          })
        }
      } catch {
        // error handled by useUploadFile
      }
    }
  }, [carrierId, asOfDate, file, buildFormData, uploadFn, uploadAsyncFn])

  const handleClose = () => {
    setCarrierId('')
    setAsOfDate('')
    setFile(null)
    setSearchQuery('')
    setCurrentPage(1)
    setAsyncTaskId(null)
    setAsyncProgress(null)
    if (importResult?.success) {
      onSuccess()
    }
    setImportResult(null)
    onClose()
  }

  // Filter & paginate unmatched rows
  const filteredRows = useMemo(() => {
    const rows = importResult?.unmatchedRows ?? []
    if (!searchQuery.trim()) return rows
    const q = searchQuery.trim().toLowerCase()
    return rows.filter(
      (row) =>
        (row.memberId && row.memberId.toLowerCase().includes(q)) ||
        (row.name && row.name.toLowerCase().includes(q))
    )
  }, [importResult?.unmatchedRows, searchQuery])

  const totalFiltered = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const paginationItems = useMemo(() => {
    const items: JSX.Element[] = []
    const maxVisible = 5
    let start = Math.max(1, safePage - Math.floor(maxVisible / 2))
    const end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1)
    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item key={p} active={p === safePage} onClick={() => setCurrentPage(p)}>
          {p}
        </Pagination.Item>
      )
    }
    return items
  }, [safePage, totalPages])

  const isWorking =
    isSyncUploading || isAsyncUploading || isPolling || fetchingUnmatched

  return (
    <Modal show={isOpen} onHide={handleClose} size='xl' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>Update Policies by Carrier</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!importResult ? (
          <>
            <Form.Group className='mb-3'>
              <Form.Label>Carrier</Form.Label>
              <Form.Select
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
                disabled={isWorking || loadingCarriers}
              >
                <option value=''>
                  {loadingCarriers ? 'Loading carriers...' : 'Select carrier...'}
                </option>
                {supportedCarriers.map((carrier) => (
                  <option key={carrier.id} value={carrier.id}>
                    {carrier.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className='text-muted'>
                Supported carriers: Ambetter, Oscar, BCBS, Blue Shield of California, Molina,
                Anthem, Imperial, Sentara, Highmark, Medica, Select Health, United
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <DateInput
                id='asOfDate'
                name='asOfDate'
                label='As-of Date'
                value={asOfDate || null}
                disabled={isWorking}
                onChange={(_field, value) => setAsOfDate(value ?? '')}
              />
              <Form.Text className='text-muted'>
                The reference date for this carrier data snapshot
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>File (CSV)</Form.Label>
              <Form.Control
                type='file'
                accept='.csv,.xlsx,.xls'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFile(e.target.files?.[0] || null)
                }}
                disabled={isWorking}
              />
            </Form.Group>

            {/* Progress bar — only visible during async processing */}
            {asyncProgress !== null && (
              <div className='mb-3'>
                <div className='d-flex justify-content-between mb-1'>
                  <small className='text-muted'>{asyncProgress.msg}</small>
                  <small className='text-muted fw-bold'>{asyncProgress.pct}%</small>
                </div>
                <ProgressBar
                  animated
                  now={asyncProgress.pct}
                  variant='primary'
                  style={{height: '10px'}}
                />
              </div>
            )}

            {fetchingUnmatched && (
              <div className='text-center text-muted mb-2'>
                <Spinner animation='border' size='sm' className='me-1' />
                Loading unmatched rows...
              </div>
            )}

            {syncError && (
              <Alert variant='danger'>
                Error: {typeof syncError === 'string' ? syncError : 'Upload failed'}
              </Alert>
            )}
          </>
        ) : (
          <>
            <Alert variant={importResult.success ? 'success' : 'danger'}>
              {importResult.message}
            </Alert>

            <div className='d-flex gap-3 mb-3'>
              <div>
                <strong>Processed:</strong>{' '}
                <Badge bg='primary' text='light'>{importResult.processedCount}</Badge>
              </div>
              <div>
                <strong>Matched:</strong>{' '}
                <Badge bg='success'>{importResult.matchedCount}</Badge>
              </div>
              <div>
                <strong>Unmatched:</strong>{' '}
                <Badge bg='warning' text='dark'>{importResult.unmatchedCount}</Badge>
              </div>
              <div>
                <strong>Errors:</strong>{' '}
                <Badge bg='danger'>{importResult.errorsCount}</Badge>
              </div>
            </div>

            {importResult.unmatchedRows && importResult.unmatchedRows.length > 0 && (
              <>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='mb-0'>
                    Unmatched Rows ({totalFiltered}
                    {searchQuery.trim() ? ` of ${importResult.unmatchedRows.length}` : ''})
                  </h6>
                  <Button
                    variant='outline-primary'
                    size='sm'
                    onClick={() => exportUnmatchedCsv(importResult.unmatchedRows, statusTitleByCode)}
                  >
                    Export Unmatched CSV
                  </Button>
                </div>

                <Form.Control
                  type='text'
                  placeholder='Search by Member ID or Name...'
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className='mb-2'
                  size='sm'
                />

                <Table size='sm' striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Member ID</th>
                      <th>Name</th>
                      <th>Carrier Status</th>
                      <th>System Status</th>
                      <th>Agent</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.rowNumber}</td>
                        <td>{row.memberId || '-'}</td>
                        <td>{row.name || '-'}</td>
                        <td>{row.carrierStatusRaw || row.carrierStatusGeneric || '-'}</td>
                        <td>
                          {row.systemPolicyStatus && statusTitleByCode[row.systemPolicyStatus] ? (
                            <BadgeStatePolicy
                              status={statusTitleByCode[row.systemPolicyStatus]}
                              color={statusColorByCode[row.systemPolicyStatus] || 'secondary'}
                            />
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {row.systemAgentName || row.systemAgentEmail ? (
                            <>
                              {row.systemAgentName || '-'}
                              {row.systemAgentEmail ? ` (${row.systemAgentEmail})` : ''}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <div className='d-flex justify-content-between align-items-center'>
                    <small className='text-muted'>
                      Page {safePage} of {totalPages}
                    </small>
                    <Pagination size='sm' className='mb-0'>
                      <Pagination.First disabled={safePage === 1} onClick={() => setCurrentPage(1)} />
                      <Pagination.Prev
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(safePage - 1)}
                      />
                      {paginationItems}
                      <Pagination.Next
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(safePage + 1)}
                      />
                      <Pagination.Last
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose} disabled={isPolling}>
          {importResult ? 'Close' : 'Cancel'}
        </Button>
        {!importResult && (
          <Button
            variant='primary'
            onClick={handleUpload}
            disabled={isWorking || !carrierId || !asOfDate || !file}
          >
            {isAsyncUploading ? (
              <>
                <Spinner animation='border' size='sm' className='me-1' /> Uploading...
              </>
            ) : isSyncUploading ? (
              <>
                <Spinner animation='border' size='sm' className='me-1' /> Processing...
              </>
            ) : isPolling || fetchingUnmatched ? (
              <>
                <Spinner animation='border' size='sm' className='me-1' /> Working...
              </>
            ) : (
              'Upload & Process'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
